<template>
    <div>
        <router-view name="contractModal"></router-view>
        <v-card flat>
            <v-card-title>
                <input-date-picker :setup.sync="datePickers.fromDate"></input-date-picker>
                <input-date-picker :setup.sync="datePickers.toDate"></input-date-picker>

                <v-divider
                    class="mx-3 hidden-md-and-down mt-0"
                    inset
                    vertical
                ></v-divider>

                <v-text-field
                    v-model="search.query"
                    @click:clear="clearQuery"
                    @keyup.enter="loadData()"
                    type="text"
                    prepend-icon="search"
                    :label="$t('common.search.field')"
                    clear-icon="cancel"
                    clearable
                    hide-details
                    solo
                    dense
                ></v-text-field>

                <v-btn
                    class="ml-2"
                    color="primary"
                    :disabled="!search.ready"
                    @click="loadData()"
                >{{ $t('common.search.button') }}</v-btn>

                <v-divider
                    class="mx-3 hidden-md-and-down mt-0"
                    inset
                    vertical
                ></v-divider>
                <template v-if="isEmployee">
                    <modal-corporate-historic :loading="loading" @close-corporate-historic-success-dialog="loadData()"></modal-corporate-historic>
                </template>
                <v-btn
                    :loading="loading"
                    :disabled="loading"
                    class="ml-1"
                    color="purple"
                    :dark="!loading"
                    @click.prevent="loadData()"
                >
                    {{ $t('common.update') }}
                </v-btn>
            </v-card-title>
            <v-divider></v-divider>
            <data-table-corporate-historic :setup.sync="dataTable" @delete-item="deleteItem" @load-data="loadData()"></data-table-corporate-historic>
        </v-card>
    </div>
</template>

<script>
import API from '@/api'
import InputDatePicker from '@/components/InputDatePicker'
import ModalCorporateHistoric from '@/components/corporate/ModalCorporateHistoric'
import DataTableCorporateHistoric from '@/components/corporate/DataTableCorporateHistoric'

export default {
    name: 'inner-tab-corporate-historic',
    components: {
        InputDatePicker,
        ModalCorporateHistoric,
        DataTableCorporateHistoric
    },
    data: function () {
        return {
            loading: true,
            datePickers: {
                fromDate: {
                    label: this.$t('corporate.constitutiveActs.fromDate'),
                    clearable: true,
                    dateFormatted: undefined,
                    hideDetails: true
                },
                toDate: {
                    label: this.$t('corporate.constitutiveActs.toDate'),
                    clearable: true,
                    dateFormatted: undefined,
                    hideDetails: true
                }
            },
            search: {
                ready: true,
                query: ''
            },
            dataTable: {
                loading: true,
                itemName: this.$t('corporate.constitutiveActs.dataTable.itemName'),
                headers: [
                    {
                        text: this.$t('corporate.constitutiveActs.dataTable.headers.date'),
                        value: 'date',
                        width: '10%',
                        align: 'center',
                        date: 'iso',
                        sortable: false
                    },
                    { text: this.$t('corporate.constitutiveActs.dataTable.headers.title'), value: 'title', width: '25%', sortable: false },
                    { text: this.$t('corporate.constitutiveActs.dataTable.headers.resume'), value: 'resume', width: '40%', sortable: false, html: true },
                    { text: this.$t('corporate.constitutiveActs.dataTable.headers.file'), value: 'file', width: '10%', align: 'center', filePreview: true, sortable: false }
                ],
                items: [],
                showInactives: false,
                totalItems: 0,
                detailTo: '',
                editTo: '',
                pagination: {
                    multiSort: true,
                    mustSort: false,
                    page: 1,
                    itemsPerPage: 500,
                    groupBy: [],
                    groupDesc: []
                },
                footerProps: {
                    itemsPerPageOptions: [500, 250, 100, 50, 25, 10]
                }
            }
        }
    },
    watch: {
        '$route': {
            handler: function (val, oldVal) {
                if (oldVal !== val) {
                    this.loadData()
                }
            },
            deep: true
        },
        'dataTable.pagination.page': {
            handler: function (val, oldVal) {
                if (oldVal !== val) {
                    this.loadData()
                }
            },
            deep: true
        },
        'dataTable.pagination.itemsPerPage': {
            handler: function (val, oldVal) {
                if (oldVal !== val) {
                    this.loadData()
                }
            },
            deep: true
        }
    },
    mounted () {
        this.adminOnly()
        this.loadData()
        window.addEventListener('storageChange', this.onStorageChange)
    },
    beforeDestroy () {
        window.removeEventListener('storageChange', this.onStorageChange)
    },
    computed: {
        isEmployee: function () {
            return this.$helpers.checkIsEmployee()
        }
    },
    methods: {
        onStorageChange () {
            this.loadData()
        },
        adminOnly: function () {
            if (this.$helpers.isAdmin()) {
                this.isAdmin = true
                this.dataTable.headers[2].width = '40%'
                this.dataTable.headers.push(
                    {
                        text: this.$t('corporate.constitutiveActs.dataTable.headers.actions'),
                        value: 'actions',
                        width: '15%',
                        align: 'center',
                        sortable: false,
                        edit: true,
                        delete: true
                    }
                )
            }
        },
        clearQuery () {
            this.search.query = ''
            this.loadData()
        },
        async loadData () {
            await this.$helpers.sleep(0)
            this.loading = true
            this.dataTable.loading = true
            this.search.ready = false
            this.dataTable.items = []
            let endpoint = API.EndPoints.getCorporateHistoric
            let where = {
                client: { __type: 'Pointer', className: 'Client', objectId: this.$helpers.getCorporateClientId(this.$route.name, `${this.$route.meta.singleName}SocietarioAtoConstitutivo`, this.$route.params.id) }
            }

            if (this.datePickers.fromDate.dateFormatted || this.datePickers.toDate.dateFormatted) {
                where['date'] = {}
            }

            if (this.datePickers.fromDate.dateFormatted) {
                where.date['$gte'] = { __type: 'Date', iso: new Date(this.datePickers.fromDate.dateFormatted.replace(/(\d{2})\/(\d{2})\/(\d{4})/, '$3-$2-$1')).toISOString() }
            }

            if (this.datePickers.toDate.dateFormatted) {
                where.date['$lte'] = { __type: 'Date', iso: new Date(this.datePickers.toDate.dateFormatted.replace(/(\d{2})\/(\d{2})\/(\d{4})/, '$3-$2-$1')).toISOString() }
            }

            if (this.search.query) {
                where['$or'] = [
                    { title: { $regex: `${this.search.query}`, $options: 'i' } },
                    { resume: { $regex: `${this.search.query}`, $options: 'i' } }
                ]
            }

            let params = {
                limit: this.dataTable.pagination.itemsPerPage,
                skip: this.dataTable.pagination.page - 1,
                count: true,
                order: '-date',
                where: JSON.stringify(where)
            }

            new API(endpoint, params).request().then(response => {
                this.dataTable.items = response.body.results
                this.dataTable.totalItems = response.body.count
                this.loading = false
                this.search.ready = true
                this.dataTable.loading = false
            }, (e) => {
                this.$helpers.requestError(e)
            })
        },
        async deleteItem (item) {
            this.loading = true
            let endpoint = API.EndPoints.removeAct
            let params = {
                objectId: item.objectId
            }
            await new API(endpoint, params).request()
            this.loadData()
            this.loading = false
        }
    }
}
</script>
